<template>
    <div>
        <v-form
            ref="form"
            v-model="valid"
            lazy-validation
            @submit.prevent="validate"
        >
            <v-alert
                dense
                outlined
                type="success"
                color="teal darken-4"
                elevation="2"
            >
                The member is eligible! You are currently requesting approval for: <br> <b>{{ String(patientCredentials.NAME).toUpperCase() }} - {{ patientCredentials.MEMB_N }}</b>
            </v-alert>
            <p style="font-weight: 700; color: #004d40;">
                Medical Provider's Information
            </p>
            <p style="color: black;">
                Please enter the information about the medical provider.
            </p>
            <v-autocomplete
                v-model.trim="data.to.hosp"
                :auto-select-first="true"
                color="#009688"
                autocomplete="nope"
                return-object
                item-text="hosp_name"
                :items="data.to.hospItems"
                :rules="[v=>!!v||'Name of Hospital or Clinic is required']"
            >
                <template #label>
                    <span style="font-weight: 500; color: black;">Name of Hospital or Clinic</span>
                </template>
            </v-autocomplete>
            <v-checkbox
                v-model="filter.toSpecial.status"
                color="#009688"
            >
                <template #label>
                    <span style="font-weight: 500; color: black;">Filter Affiliated Doctors by Specialization</span>
                </template>
            </v-checkbox>
            <v-expand-transition>
                <div v-show="filter.toSpecial.status">
                    <p>This will filter the doctors at <strong>{{ data.to.hosp.hosp_name }}</strong> with the selected specialization.</p>
                    <v-combobox
                        v-if="getSpecializations"
                        ref="filterToSpecialFieldVal"
                        v-model="filter.toSpecial.fieldVal"
                        clearable
                        :items="getSpecializations"
                        item-text="sp_name"
                        return-object
                        @click:clear="resetFilterToSpecialFieldVal"
                    >
                        <template #label>
                            <span style="font-weight: 500; color: black;">Specialization</span>
                        </template>
                    </v-combobox>
                </div>
            </v-expand-transition>
            <v-alert
                dense
                outlined
                type="info"
                color="#003566"
                elevation="2"
            >
                Enter the doctor's name in the format <strong>Last Name, First Name (e.g., DELA CRUZ, JUAN)</strong>
            </v-alert>
            <v-combobox
                ref="searchDoctor"
                v-model.trim="data.to.doc"
                hint="Surname, First Name"
                clearable
                :items="providerAffiDoctors && providerAffiDoctors.data ? providerAffiDoctors.data : [{doc_name: '', doc_id: ''}]"
                item-text="doc_name"
                return-object
                :placeholder="`Search Doctors from ${data.to.hosp.hosp_name}`"
                :rules="rules.doctor.on"
                :prepend-inner-icon="mdiMagnify"
                prefix="DR."
                :search-input.sync="docConf.searchInput"
                autocomplete="nope"
                color="teal accent-4"
                @keyup.enter.prevent="saveNewDoctor"
                @click:clear="clearSearchDoctor"
                @blur="saveNewDoctor"
                @focus="focusDoctor()"
            >
                <template #label>
                    <span :style="`font-weight: 500; color: ${color.doctor};`">Full Name of Doctor</span>
                </template>
                <template #no-data>
                    <p v-if="docConf.searchInput" class="errSearchDoctor">
                        Use <strong>"DR. {{ docConf.searchInput.toUpperCase() }}"</strong>?
                        <v-chip
                            color="teal accent-4"
                            text-color="white"
                            @click.stop="saveNewDoctor"
                        >
                            Continue
                        </v-chip>
                    </p>
                    <p v-else-if="filter.toSpecial.fieldVal && filter.toSpecial.fieldVal.sp_desc" class="errSearchDoctor">
                        There are currently <strong>no doctors affiliated</strong> with your selected hospital who specialize in <strong>{{ filter.toSpecial.fieldVal.sp_name }}</strong><br><b><span style="color: darkgreen;">If you cannot find the doctor, you may still enter their name and press "Continue".</span></b>
                    </p>
                    <p v-else class="errSearchDoctor">
                        There are currently <strong>no doctors affiliated</strong> with your selected hospital listed in our system.<br><b><span style="color: darkgreen;">If you cannot find the doctor, you may still enter their name and press "Continue."</span></b>
                    </p>
                </template>
                <template #item="{ item }">
                    <v-list-item-content>
                        <v-list-item-title style="color: black;">
                            {{ item.doc_name }}
                        </v-list-item-title>
                        <v-list-item-subtitle style="font-size: 12px;">
                            {{ getDoctorSpecializations(item) }}
                        </v-list-item-subtitle>
                    </v-list-item-content>
                </template>
            </v-combobox>
            <v-expand-transition>
                <div v-if="showSaveBtn && !showSaveBtn2 && docConf.enterBtnShowOnDocBlur">
                    <v-card style="padding: 25px; margin-bottom: 15px">
                        <p style="color: black; font-weight: 700;">
                            Is the doctor a Fellow/Diplomate or Non-Fellow/Non-Diplomate?
                        </p>
                        <v-radio-group
                            v-model="data.docMembershipCategories"
                            mandatory
                        >
                            <v-radio
                                color="teal darken-1"
                                :value="true"
                            >
                                <template #label>
                                    <span style="color: black">Fellow/Diplomate</span>
                                </template>
                            </v-radio>
                            <v-radio
                                color="teal darken-1"
                                :value="false"
                            >
                                <template #label>
                                    <span style="color: black">Non-Fellow/Non-Diplomate</span>
                                </template>
                            </v-radio>
                        </v-radio-group>
                    </v-card>
                </div>
            </v-expand-transition>
            <v-radio-group
                v-model="data.medico_legal"
                column
                :rules="[v=> v !== null || 'This question is required']"
            >
                <template #label>
                    <div style="font-family: sans-serif; font-size: 16px; color: black">
                        Is this consultation for cases related to a vehicular accident or other medico-legal cases?
                    </div>
                </template>
                <v-radio
                    color="teal darken-1"
                    label="Yes"
                    :value="true"
                />
                <v-radio
                    color="teal darken-1"
                    label="No"
                    :value="false"
                />
            </v-radio-group>
            <v-textarea
                ref="reason"
                v-model.trim="data.to.reason"
                clearable
                :rules="rules.reason"
                auto-grow
                rows="1"
                row-height="1"
                autocomplete="nope"
                color="#009688"
                @blur="changeColor('reason', 'reason')"
            >
                <template #label>
                    <span :style="`font-weight: 500; color: ${color.reason};`">Reason for Consultation</span>
                </template>
            </v-textarea>
            <v-alert
                color="teal darken-4"
                type="info"
                outlined
                variant="tonal"
                density="compact"
                rounded="lg"
                class="custom-alert"
            >
                <strong>Why is an email address required?</strong><br>
                The member will receive a copy of this request via email for their reference and records.
            </v-alert>
            <v-text-field
                v-if="membEmail&&membEmail.data === null"
                key="addEmail"
                ref="email"
                v-model.trim="data.to.email"
                autocomplete="nope"
                color="#009688"
                :rules="rules.email"
                type="email"
                clearable
                :disabled="false"
                :name="`email_${randomFieldName}`"
                @paste.prevent
                @copy.prevent
                @cut.prevent
                @contextmenu.prevent
                @blur="changeColor('email', 'email')"
            >
                <template #label>
                    <span :style="`font-weight: 500; color: ${color.email};`">Member's Email Address</span>
                </template>
            </v-text-field>
            <v-text-field
                v-else
                key="showEmail"
                ref="email"
                v-model.trim="data.to.showEmail"
                color="#009688"
                :disabled="true"
            >
                <template #label>
                    <span style="font-weight: 500; color: black;">Member's Email Address</span>
                </template>
            </v-text-field>
            <v-checkbox
                v-if="membEmail && membEmail.data === null"
                key="showEmailConfirm"
                ref="emailConfirm"
                v-model="data.emailConfirm"
                color="teal darken-1"
                :rules="rules.emailConfirm"
            >
                <template #label>
                    <span :style="`font-weight: 500; color: ${color.emailConfirm};`">I confirm that the email provided belongs to the patient: <b v-if="patientCredentials">{{ String(patientCredentials.NAME).toUpperCase() }}</b></span>
                </template>
            </v-checkbox>
            <v-btn
                color="#009688 white--text"
                style="margin: 2% 0 7% 0; background-color: #009688; color: white;"
                :disabled="!valid || !docConf.enterBtnShowOnDocBlur"
                :loading="isLoading"
                type="submit"
            >
                <v-icon>{{ mdiArrangeSendToBack }}</v-icon>Submit
            </v-btn>
        </v-form>
    </div>
</template>

<script>
import { mdiArrangeSendToBack, mdiMagnify } from '@mdi/js'
import { mapActions, mapGetters } from 'vuex'
export default {
    props: ['patientCredentials'],
    data: ()=>({
        mdiArrangeSendToBack, mdiMagnify,
        randomFieldName: Math.random().toString(36).substr(2, 9),
        color: {
            reason: 'black',
            doctor: 'black',
            email: 'black',
            emailConfirm: 'black'
        },
        filter: {
            toSpecial: {
                fieldVal: '',
                status: false
            }
        },
        data: {
            medico_legal: null,
            emailConfirm: false,
            docMembershipCategories: false,
            isRefferal: false,
            to: {
                hosp: {
                    hosp_name: '',
                    hosp_msu_code: ''
                },
                hospItems: null,
                doc: null,
                reason: '',
                email: '',
                showEmail: ''
            }
        },
        rules: {
            emailConfirm: [v => !!v || 'This checkbox is required'],
            email: [v => !!v || 'Email Address is required' , (v) => /^[a-zA-Z0-9]+([._%-][a-zA-Z0-9]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/.test(v) || 'E-mail Address must be valid'],
            reason: [v => !!v || 'Reason for Consultation is Required', v => /[a-zA-Z]/.test(v) || 'Please provide a valid reason!'],
            doctor: {
                def: [()=>true],
                off: [()=>true],
                on: [
                    v =>
                        /^[A-Za-zñÑ\-' .()""]+, ?[A-Za-zñÑ\-' .()""]+$/.test(v && v.doc_name ? v.doc_name : v) ||
                        'Doctor Name must follow the pattern "Surname, First Name" and only contain letters, hyphens, apostrophes, periods, spaces, quotes, parentheses, and ñÑ'
                ]
            }
        },
        screen_width: document.documentElement.clientWidth + 9,
        docConf: {
            searchInput: null,
            enterBtnShow: false,
            enterBtnShowOnDocBlur: true
        },
        isLoading: false,
        valid: false
    }),
    computed: {
        ...mapGetters(['providerAffiDoctors', 'docInfoData', 'approvalConsultation', 'getSpecializations', 'membEmail', 'getLinkedProvidersData']),
        showSaveBtn() {
            this.setValidateDoctorName()
            return this.data.to.doc && this.data.to.doc.doc_id === null ? true : false
        },
        showSaveBtn2() {
            if (this.docConf.searchInput === '') return false
            if (this.providerAffiDoctors && this.providerAffiDoctors.data) {
                const str = this.docConf.searchInput ? this.docConf.searchInput.toUpperCase() : ''
                const query = this.providerAffiDoctors.data.filter(obj=>obj.doc_name === str)
                return query.length > 0
            }
            return false
        }
    },
    watch: {
        getLinkedProvidersData(newVal) {
            if (newVal) this.data.to.hospItems = [this.data.to.hosp, ...this.getLinkedProvidersData.data]
        },
        membEmail(newVal) {
            if (newVal.data) {
                this.data.to.email      = newVal.data
                this.data.to.showEmail  = this.censorEmail(newVal.data)
            }
        },
        approvalConsultation(newVal) {
            if (newVal) this.$emit('consultationStatus', newVal.data)
        },
        docInfoData(newVal) {
            if (newVal) {
                this.data.to.hosp       = {
                    hosp_name: newVal.NAME,
                    hosp_msu_code: this.$cookies.get('provider_id')
                }
                this.data.to.hospItems  = [this.data.to.hosp]
            }
        },
        'filter.toSpecial.fieldVal': {
            async handler(newVal) {
                if (newVal) {
                    this.filter.toSpecial.fieldVal.sp_desc = newVal.sp_desc
                    this.filter.toSpecial.fieldVal.sp_name = newVal.sp_name
                    const PROVIDER_ID       = this.data.to.hosp.hosp_msu_code
                    const SPECIALIZATION    = newVal.sp_desc
                    await this.getProviderAffiDoctors({ PROVIDER_ID, SPECIALIZATION })
                } else {
                    this.resetFilterToSpecialFieldVal()
                }
                // this.setDoctorName()
            }
        },
        'filter.toSpecial.status': {
            handler(newVal) {
                // console.log('filter.toSpecial.status newVal: ', newVal)
                if (!newVal) this.resetFilterToSpecialFieldVal()
            }
        },
        'data.to.reason': {
            handler() {
                this.changeColor('reason', 'reason')
            }
        },
        'data.to.email': {
            handler() {
                this.$nextTick(()=>{
                    this.changeColor('email', 'email')
                })
            }
        },
        'data.emailConfirm': {
            handler () {
                this.$nextTick(()=>{
                    this.changeColor('emailConfirm', 'emailConfirm')
                })
            }
        },
        'data.to.hosp': {
            async handler(newVal) {
                if (newVal) {
                    await this.getHospClinDocData(newVal.hosp_msu_code)
                    this.data.isRefferal = newVal.hosp_msu_code !== this.$cookies.get('provider_id')
                }
            }
        },
        'data.to.doc': {
            async handler(newVal) {
                if (newVal) {
                    this.docConf.enterBtnShow = false
                    if (typeof newVal !== 'object') {
                        this.data.to.doc = {
                            doc_id: null,
                            doc_name: newVal.toUpperCase()
                        }
                    }
                    // console.log('data.to.doc enterBtnShowOnDocBlur: ', this.enterBtnShowOnDocBlur = false)
                }
            }
        },
        'docConf.searchInput': {
            async handler() {
                this.docConf.enterBtnShow = true
            }
        }
    },
    async mounted() {
        if (this.patientCredentials===null)
            this.$emit('reCheckMembEligibility')
        else {
            const PROVIDER_ID = this.$cookies.get('provider_id')
            await this.getProviderInfo({ PROVIDER_ID })
            await this.specializations()
            await this.getMembEmail({ MEMB_N: this.patientCredentials.MEMB_N })
            await this.getLinkedProviders({ MSU_C: PROVIDER_ID })
        }
    },
    methods: {
        ...mapActions(['getProviderAffiDoctors', 'getProviderInfo', 'generateApprovalConsultation', 'getCsrfToken', 'specializations', 'toDoctors', 'getMembEmail', 'getLinkedProviders']),
        getDoctorSpecializations(doctor) {
            return [
                doctor.doc_specialization_1,
                doctor.doc_specialization_2,
                doctor.doc_specialization_3
            ]
                .filter(Boolean)
                .join(' • ')
        },
        setValidateDoctorName() {
            this.docConf.enterBtnShowOnDocBlur = this.validateDoctorName(this.data.to.doc)
        },
        validateDoctorName(doctor) {
            const rules = this.rules.doctor.on
            let isValid = true

            for (const rule of rules) {
                const result = rule(doctor)
                if (result !== true) {
                    isValid = false
                    break
                }
            }

            return isValid
        },
        focusDoctor() {
            this.docConf.enterBtnShow = true
            this.docConf.enterBtnShowOnDocBlur = false
        },
        blurSearchDoctorField() {
            this.$nextTick(()=>{
                this.$refs.searchDoctor.blur()
                this.$nextTick(()=>{
                    this.changeColor('searchDoctor', 'doctor')
                })
            })
        },
        changeRulesToDoc(params) {
            this.docConf.enterBtnShow = false
            this.rules.doctor.def = params
                ? this.rules.doctor.on
                : this.rules.doctor.off

            this.blurSearchDoctorField()
        },
        clearSearchDoctor() {
            this.data.to.doc = { doc_id: null, doc_name: '' }
            // this.changeRulesToDoc(false)
            this.blurSearchDoctorField()
        },
        saveNewDoctor() {
            // this.changeRulesToDoc(this.data.to.doc !== null)
            this.$nextTick(() => {
                this.docConf.enterBtnShowOnDocBlur = this.validateDoctorName(this.data.to.doc)
                this.blurSearchDoctorField()
            })
        },
        censorEmail(email) {
            const [local, domain] = email.split('@')
            // Censor the local part
            const censoredLocal = local.length > 4
                ? `${local.slice(0, 2)}${'*'.repeat(local.length - 3)}${local.slice(-1)}`
                : local

            // Censor the domain part
            const censoredDomain = domain.length > 5
                ? `${domain.slice(0, 1)}${'*'.repeat(domain.length - 2)}${domain.slice(-1)}`
                : domain

            return `${censoredLocal}@${censoredDomain}`
        },
        changeColor(refName, colorKey) {
            this.$nextTick(() => {
                if (this.$refs && this.$refs[refName])
                    this.$refs[refName].validate() ? this.color[colorKey] = 'black' : this.color[colorKey] = 'red'
            })
        },
        customFilterToDoctor(item, queryText, itemText) {
            const normalizeSpaces   = (str) => str.replace(/\s+/g, ' ').trim()
            const text              = normalizeSpaces(itemText.toString().toUpperCase())
            const searchText        = normalizeSpaces(queryText.toString().toUpperCase())
            return text.includes(searchText)
        },
        resetFilterToSpecialFieldVal() {
            this.$nextTick(()=>{
                if (this.$refs&&this.$refs.filterToSpecialFieldVal) this.$refs.filterToSpecialFieldVal.blur()
                this.filter.toSpecial.fieldVal  = { sp_desc: '', sp_name: '' }
                this.data.to.doc                = { doc_name: '', doc_id: null }
                this.docConf                    = { searchInput: null }
            })
        },
        setDoctorName(name='', id=null) {
            this.$nextTick(()=>{
                this.data.to.doc = {
                    doc_name: name,
                    doc_id: id
                }
            })
        },
        async validate () {
            this.changeColor('reason', 'reason')
            this.changeColor('email', 'email')
            this.changeColor('emailConfirm', 'emailConfirm')
            this.changeColor('searchDoctor', 'doctor')
            this.$nextTick(async ()=>{
                if (this.$refs.form.validate()) {
                    this.isLoading  = true
                    const arg       = {
                        MEMB_N: this.patientCredentials.MEMB_N,
                        TOKEN: this.patientCredentials.TOKEN,
                        MEMB_EMAIL: this.data.to.email ? this.data.to.email : null,
                        REQUEST: {
                            IS_REFERRAL: this.data.isRefferal,
                            FR_HOSP: this.data.isRefferal ? this.$cookies.get('provider_id') : null,
                            FR_DOC: null,
                            FR_DOC_NAME: '',
                            TO_HOSP: this.data.to.hosp.hosp_msu_code,
                            TO_DOC: this.data.to.doc && this.data.to.doc.doc_id ? parseInt(this.data.to.doc.doc_id) : null,
                            TO_DOC_NAME: this.data.to.doc && this.data.to.doc.doc_name ? this.data.to.doc.doc_name : '',
                            TO_DOC_FELLOW_DIPLOMATE: this.data.docMembershipCategories,
                            CHIEF_C: `${this.data.to.reason} \nVEHICULAR ACCIDENT OR MEDICO-LEGAL CASE? ${this.data.medico_legal ? 'YES' : 'NO'}`,
                            MEDICO_LEGAL: this.data.medico_legal
                        }
                    }
                    await this.generateApprovalConsultation(arg)
                }
            })
        },
        async getHospClinDocData(PROVIDER_ID) {
            await this.getProviderAffiDoctors({ PROVIDER_ID })
            // await this.toDoctors({ PTYPE: '', HOSP_MSU: PROVIDER_ID, SPEC: '' })
        }
    }
}
</script>

<style scoped>
.errSearchDoctor {
    padding: 5px 5px 0 5px;
    margin: 0;
}
</style>
